import { type ChangePlanFeature, type ChangePlanPlan } from '@clickguard/design-system'
import {
	DiscountInfoDTO,
	PricingTierInfoDTO,
	PricingTierPriceInfoDTO,
	SubscriptionDTO
} from 'api-models'
import { planOptions } from 'pages/start/standard/v2/plan-payment/data/plan-features'
import { PlanFeature } from 'pages/start/standard/v2/plan-payment/plan-selection/types'

// Shared presentation for the two ChangePlanModal surfaces — "Change
// subscription plan" (change-subscription-plan.tsx) and "New subscription"
// (new-subscription-plan.tsx). Both render the identical DS card, so the
// cycle tabs, feature copy, price formatting and plan-row builder live here
// once. Each surface keeps its own submit wiring + custom-plan footer.

export type BillingPeriod = 'monthly' | 'quarterly' | 'yearly'

// Same period → Stripe interval mapping the legacy ProtectionPlan tier used.
export const PERIOD_TO_INTERVAL: Record<BillingPeriod, 'month' | 'year'> = {
	monthly: 'month',
	quarterly: 'month',
	yearly: 'year'
}
export const PERIOD_TO_COUNT: Record<BillingPeriod, number> = {
	monthly: 1,
	quarterly: 3,
	yearly: 1
}

export const getSubscriptionBillingPeriod = (
	subscription?: Pick<SubscriptionDTO, 'type' | 'plan' | 'next_plan'> | null
): BillingPeriod => {
	// Cancelled subscriptions retain their last plan. A trial's paid plan is
	// stored in next_plan; its own plan has a daily interval.
	const plan =
		subscription?.type === 'TRIAL'
			? subscription.next_plan || subscription.plan
			: subscription?.plan

	return (
		(Object.keys(PERIOD_TO_INTERVAL) as BillingPeriod[]).find(
			(period) =>
				plan?.interval === PERIOD_TO_INTERVAL[period] &&
				plan?.interval_count === PERIOD_TO_COUNT[period]
		) || 'yearly'
	)
}

export const PERIOD_WORD: Record<BillingPeriod, string> = {
	monthly: 'monthly',
	quarterly: 'quarterly',
	yearly: 'annually'
}

// The plans endpoint returns brand-prefixed names ("ClickGUARD Lite") and
// long marketing descriptions; the DS card shows the bare tier name with a
// short qualifier. Strip the prefix and take the copy from the DS.
const stripBrand = (name: string) => name.replace(/^ClickGUARD\s+/i, '')

export const PLAN_DESCRIPTIONS: Record<string, string> = {
	lite: 'For single-site businesses',
	standard: 'For growing teams',
	pro: 'For high-traffic campaigns'
}

// DS copy for a rejected code. billingStore.fetchCoupon rejects with an
// already-parsed message string, so a second parseError() would swallow it and
// return the generic "Unexpected error" — pass the string through instead, and
// replace the two backend not-found/unavailable wordings with the DS message.
const COUPON_INVALID = 'This code is invalid or has expired'
const COUPON_INVALID_BACKEND = [
	'Promotion code not found',
	'This coupon code is no longer available'
]

export const couponErrorMessage = (err: unknown) => {
	const message =
		typeof err === 'string'
			? err
			: (err as { response?: { data?: { message?: string } } })?.response?.data
					?.message
	if (!message || COUPON_INVALID_BACKEND.includes(message)) return COUPON_INVALID
	return message
}

// Header cycle tabs (Figma 873:12612 — savings pills float under the tab).
export const CYCLES = [
	{ id: 'monthly', label: 'Monthly' },
	{ id: 'quarterly', label: 'Quarterly', savings: 'save 10%' },
	{ id: 'yearly', label: 'Annually', savings: 'save 20%' }
]

// Expanded-body feature rows per tier. The copy is owned by the onboarding
// plan data (plan-features.ts) so both plan surfaces stay in sync — this only
// adapts it to the DS shape. Labels there mark emphasis with **double
// asterisks**, and rows the tier doesn't include carry `available: false` plus
// the `requiredPlan` that does.
const renderFeatureLabel = (label: string) => {
	const parts = label.split('**')

	if (parts.length === 1) return label

	return parts.map((part, index) =>
		index % 2 === 1 ? <strong key={index}>{part}</strong> : part
	)
}

const toChangePlanFeature = (feature: PlanFeature): ChangePlanFeature => ({
	label: (
		<>
			{renderFeatureLabel(feature.label)}
			{!feature.available && feature.requiredPlan && (
				<strong> ({feature.requiredPlan} plan)</strong>
			)}
		</>
	),
	locked: !feature.available
})

export const FEATURES: Record<string, ChangePlanFeature[]> = planOptions.reduce(
	(acc, plan) => {
		acc[plan.id] = plan.features.map(toChangePlanFeature)
		return acc
	},
	{} as Record<string, ChangePlanFeature[]>
)

export const getPrices = (plan: PricingTierInfoDTO, period: BillingPeriod) =>
	plan.prices.filter(
		(p) =>
			p.interval === PERIOD_TO_INTERVAL[period] &&
			p.interval_count === PERIOD_TO_COUNT[period]
	)

export const getBasePrice = (plan: PricingTierInfoDTO, period: BillingPeriod) => {
	const prices = getPrices(plan, period)
	return prices.find((p) => p.price_monthly) || prices[0] || null
}

// Display-only discount (real charge is computed server-side). Mirrors the
// order of utils/formatCurrencyDiscount (amount first, then percent).
export const applyDiscount = (cents: number, coupon: DiscountInfoDTO | null) => {
	if (!coupon) return cents
	let v = cents
	if (coupon.amount_off) v = v - coupon.amount_off
	if (coupon.percent_off) v = v - v * (coupon.percent_off / 100)
	return Math.max(0, v)
}

// Cents → "$1,143.20". Unlike utils/formatCurrency this does NOT floor, so
// discounted amounts keep their cents to match the Figma pricing ($95.20).
export const money = (cents = 0) =>
	`$${(cents / 100).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})}`

// Scoped padding override for the config row (Figma 873:12258): the DS renders
// the ad-spend field as AppButton `lg` (min-h-40 / py-10 / px-16) and the coupon
// as Input `md` (py-10 / px-14) — both chunkier than Figma's 36px box with
// 8×12 padding, and mismatched with each other. Pin both to the Figma box so
// they align. The ad-spend trigger is the only [aria-haspopup]; the only text
// inputs in the modal are the coupon + custom-spend fields. Remove once the DS
// ChangePlanModal ships the correct field size.
export const CONFIG_FIELDS_FIX = 'cg-cpm-config-fields'
export const configFieldsFixCss = `
.${CONFIG_FIELDS_FIX} [aria-haspopup]{min-height:36px!important;padding:8px 12px!important}
.${CONFIG_FIELDS_FIX} input{padding:8px 12px!important}
`

// Right-aligned price lines for a plan row (with strikethrough on the selected
// row when a coupon is applied).
export const makePriceLines = (
	priceObj: PricingTierPriceInfoDTO | null,
	selected: boolean,
	cycle: BillingPeriod,
	coupon: DiscountInfoDTO | null
): Partial<ChangePlanPlan> => {
	if (!priceObj) return { price: 'Not available' }
	const monthly = `${money(priceObj.price_monthly)} /month`
	const total =
		cycle === 'monthly'
			? undefined
			: `${money(priceObj.price)} ${PERIOD_WORD[cycle]}`

	if (selected && coupon) {
		const discMonthly = `${money(
			applyDiscount(priceObj.price_monthly ?? 0, coupon)
		)} /month`
		const discTotal =
			cycle === 'monthly'
				? undefined
				: `${money(applyDiscount(priceObj.price, coupon))} ${PERIOD_WORD[cycle]}`
		return {
			originalPrice: monthly,
			originalPriceCaption: total,
			price: discMonthly,
			priceCaption: discTotal
		}
	}

	return {
		price: selected ? monthly : `from ${monthly}`,
		priceCaption: total
	}
}

// The tier plan rows (Lite/Standard/Pro) shared by both modals. Each surface
// appends its own Custom-plan card (the footer action differs) after this.
export const buildTierPlans = ({
	plans,
	selectedPlanId,
	selectedPrice,
	cycle,
	coupon
}: {
	plans: PricingTierInfoDTO[]
	selectedPlanId: string | null
	selectedPrice: PricingTierPriceInfoDTO | null
	cycle: BillingPeriod
	coupon: DiscountInfoDTO | null
}): ChangePlanPlan[] =>
	plans.map((plan) => {
		const selected = plan.tier === selectedPlanId
		const priceObj = selected ? selectedPrice : getBasePrice(plan, cycle)
		return {
			id: plan.tier,
			name: stripBrand(plan.name),
			description: PLAN_DESCRIPTIONS[plan.tier] ?? plan.description,
			badge: plan.tier === 'standard' ? 'Recommended' : undefined,
			features: FEATURES[plan.tier],
			...makePriceLines(priceObj, selected, cycle, coupon)
		}
	})
